import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import {
  PaginatedResult,
  PaginationParams,
  createPaginatedResult,
} from '@core/common/models/pagination.models';
import {
  ModifierGroupDefinitionMutationResult,
  ProductModifierGroupDefinition,
} from '@features/catalog/models/product-modifier-definition.models';
import { ProductModifierDraftSelectionType } from '@features/catalog/models/product-form-draft.models';
import {
  GraphqlMutationModifierGroupDefinitionPayloadDto,
  GraphqlProductModifierGroupDefinitionByIdDataDto,
} from './dto/product-modifier-definition.dto';
import {
  asModifierGroupDefinitionDtoArray,
  mapMutationModifierGroupDefinitionPayload,
  mapProductModifierGroupDefinitionDto,
} from './mappers/product-modifier-definition.mapper';
import { ensureNoTopLevelErrors, GraphqlHttpResponse, mapEntityMutationResult, mapLifecycleMutationResult } from './mappers/graphql-error.mapper';

const LIST_QUERY = `
  query GetProductModifierGroupDefinitions($activeOnly: Boolean = true, $name: String, $limit: Int = 50, $offset: Int = 0) {
    productModifierGroupDefinitions(activeOnly: $activeOnly, name: $name, limit: $limit, offset: $offset) {
      items {
        id
        companyId
        name
        slug
        description
        selectionType
        required
        minSelections
        maxSelections
        position
        active
        productsCount
        productModifierOptionDefinitions(activeOnly: true) {
          id
          name
          description
          priceDelta
          maxQuantity
          position
          active
        }
      }
      total
      limit
      offset
    }
  }
`;

const BY_ID_QUERY = `
  query GetProductModifierGroupDefinitionById($id: ID!) {
    productModifierGroupDefinition(id: $id) {
      id
      name
      slug
      description
      selectionType
      required
      minSelections
      maxSelections
      position
      active
      productsCount
      productModifierOptionDefinitions(activeOnly: true) {
        id
        name
        description
        priceDelta
        maxQuantity
        position
        active
      }
    }
  }
`;

const CREATE_MUTATION = `
  mutation CreateProductModifierGroupDefinition(
    $name: String!
    $slug: String
    $description: String
    $selectionType: ProductModifierSelectionTypeEnum
    $required: Boolean
    $minSelections: Int
    $maxSelections: Int
    $position: Int
    $active: Boolean
  ) {
    createProductModifierGroupDefinition(
      name: $name
      slug: $slug
      description: $description
      selectionType: $selectionType
      required: $required
      minSelections: $minSelections
      maxSelections: $maxSelections
      position: $position
      active: $active
    ) {
      definition {
        id
        name
        slug
        description
        selectionType
        required
        minSelections
        maxSelections
        position
        active
      }
      errors
    }
  }
`;

const UPDATE_MUTATION = `
  mutation UpdateProductModifierGroupDefinition(
    $id: ID!
    $name: String
    $slug: String
    $description: String
    $selectionType: ProductModifierSelectionTypeEnum
    $required: Boolean
    $minSelections: Int
    $maxSelections: Int
    $position: Int
    $active: Boolean
  ) {
    updateProductModifierGroupDefinition(
      id: $id
      name: $name
      slug: $slug
      description: $description
      selectionType: $selectionType
      required: $required
      minSelections: $minSelections
      maxSelections: $maxSelections
      position: $position
      active: $active
    ) {
      definition {
        id
        name
        slug
        description
        selectionType
        required
        minSelections
        maxSelections
        position
        active
      }
      errors
    }
  }
`;

const DESTROY_MUTATION = `
  mutation DestroyProductModifierGroupDefinition($id: ID!) {
    destroyProductModifierGroupDefinition(id: $id) {
      definition { id name active }
      errors
    }
  }
`;

const PERMANENTLY_DELETE_MUTATION = `
  mutation PermanentlyDeleteProductModifierGroupDefinition($id: ID!) {
    permanentlyDeleteProductModifierGroupDefinition(id: $id) {
      success
      errors
    }
  }
`;

@Injectable()
export class ModifierGroupDefinitionApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string
  ) {}

  private graphqlUrl(): string {
    return `${this.baseUrl}${ApiEndpoints.graphql}`;
  }

  list(
    filters?: { activeOnly?: boolean | null; name?: string | null },
    pagination?: PaginationParams
  ): Observable<PaginatedResult<ProductModifierGroupDefinition>> {
    const activeOnly = filters?.activeOnly === undefined ? true : filters.activeOnly;
    const name = filters?.name?.trim() ? filters.name.trim() : null;
    const limit = pagination?.limit ?? 50;
    const offset = pagination?.offset ?? 0;
    return this.http
      .post<
        GraphqlHttpResponse<{
          productModifierGroupDefinitions: {
            items: unknown[];
            total: number;
            limit: number;
            offset: number;
          };
        }>
      >(this.graphqlUrl(), {
        query: LIST_QUERY,
        variables: { activeOnly, name, limit, offset },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const definitions = (data.productModifierGroupDefinitions?.items ?? []).map((item) =>
            mapProductModifierGroupDefinitionDto(item as never)
          );
          return createPaginatedResult({
            items: definitions,
            total: data.productModifierGroupDefinitions?.total ?? 0,
            limit: data.productModifierGroupDefinitions?.limit ?? limit,
            offset: data.productModifierGroupDefinitions?.offset ?? offset,
          });
        })
      );
  }

  getById(id: string): Observable<ProductModifierGroupDefinition | null> {
    return this.http
      .post<GraphqlHttpResponse<GraphqlProductModifierGroupDefinitionByIdDataDto>>(this.graphqlUrl(), {
        query: BY_ID_QUERY,
        variables: { id },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const list = asModifierGroupDefinitionDtoArray(data.productModifierGroupDefinition);
          return list[0] ? mapProductModifierGroupDefinitionDto(list[0]) : null;
        })
      );
  }

  create(input: {
    name: string;
    slug?: string | null;
    description?: string | null;
    selectionType?: ProductModifierDraftSelectionType;
    required?: boolean;
    minSelections?: number;
    maxSelections?: number | null;
    position?: number | null;
    active?: boolean;
  }): Observable<ModifierGroupDefinitionMutationResult> {
    const variables: Record<string, unknown> = {
      name: input.name.trim(),
      description: input.description?.trim() || null,
      selectionType: input.selectionType ?? 'single',
      required: input.required ?? false,
      minSelections: input.minSelections ?? 0,
      maxSelections: input.maxSelections ?? null,
      position: input.position ?? null,
      active: input.active ?? true,
    };
    const slugTrimmed = input.slug?.trim();
    if (slugTrimmed) {
      variables['slug'] = slugTrimmed;
    }
    return this.http
      .post<
        GraphqlHttpResponse<{
          createProductModifierGroupDefinition?: GraphqlMutationModifierGroupDefinitionPayloadDto | null;
        }>
      >(this.graphqlUrl(), { query: CREATE_MUTATION, variables })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => mapMutationModifierGroupDefinitionPayload(data.createProductModifierGroupDefinition))
      );
  }

  update(input: {
    id: string;
    name?: string;
    slug?: string | null;
    description?: string | null;
    selectionType?: ProductModifierDraftSelectionType;
    required?: boolean;
    minSelections?: number;
    maxSelections?: number | null;
    position?: number | null;
    active?: boolean;
  }): Observable<ModifierGroupDefinitionMutationResult> {
    const variables: Record<string, unknown> = { id: input.id };
    if (input.name !== undefined) variables['name'] = input.name.trim();
    if (input.slug !== undefined) {
      const s = input.slug?.trim();
      variables['slug'] = s === '' ? null : s;
    }
    if (input.description !== undefined) variables['description'] = input.description?.trim() || null;
    if (input.selectionType !== undefined) variables['selectionType'] = input.selectionType;
    if (input.required !== undefined) variables['required'] = input.required;
    if (input.minSelections !== undefined) variables['minSelections'] = input.minSelections;
    if (input.maxSelections !== undefined) variables['maxSelections'] = input.maxSelections;
    if (input.position !== undefined) variables['position'] = input.position;
    if (input.active !== undefined) variables['active'] = input.active;
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProductModifierGroupDefinition?: GraphqlMutationModifierGroupDefinitionPayloadDto | null;
        }>
      >(this.graphqlUrl(), { query: UPDATE_MUTATION, variables })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => mapMutationModifierGroupDefinitionPayload(data.updateProductModifierGroupDefinition))
      );
  }

  destroy(id: string): Observable<ModifierGroupDefinitionMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          destroyProductModifierGroupDefinition?: GraphqlMutationModifierGroupDefinitionPayloadDto | null;
        }>
      >(this.graphqlUrl(), { query: DESTROY_MUTATION, variables: { id } })
      .pipe(
        map((res) =>
          mapEntityMutationResult(res, 'destroyProductModifierGroupDefinition', (payload) =>
            mapMutationModifierGroupDefinitionPayload(
              payload as GraphqlMutationModifierGroupDefinitionPayloadDto | null | undefined
            )
          )
        )
      );
  }

  updateActive(id: string, active: boolean): Observable<ModifierGroupDefinitionMutationResult> {
    return this.update({ id, active });
  }

  permanentlyDelete(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          permanentlyDeleteProductModifierGroupDefinition?: { success?: boolean; errors?: string[] } | null;
        }>
      >(this.graphqlUrl(), { query: PERMANENTLY_DELETE_MUTATION, variables: { id } })
      .pipe(map((res) => mapLifecycleMutationResult(res, 'permanentlyDeleteProductModifierGroupDefinition')));
  }
}
